import includes from 'lodash/includes'
import split from 'lodash/split'
import type { ImageLoaderProps } from 'next/image'
import type { CSSProperties } from 'react'

// The image URL builders, kept apart from the components so a React Server Component can build an image's
// URLs without importing the client components that sit beside them (@Components/Image pulls next/image,
// next/head and hooks). Pure functions, no React: safe from either side of the boundary.

export interface ContentstackLoaderProps extends Omit<ImageLoaderProps, 'quality'> {
  cropRatio?: string
  objectFit?: CSSProperties['objectFit']
  objectPosition?: CSSProperties['objectPosition']
  quality?: number | string
}

export const contentstackLoader = (
  {
    cropRatio,
    objectFit,
    objectPosition = 'center top',
    quality = '75,90',
    src,
    width,
  }: ContentstackLoaderProps,
) => {
  if (!includes(src, 'contentstack')) {
    return src
  }

  const url = new URL(src)

  url.searchParams.set('format', 'pjpg')
  url.searchParams.set('auto', 'webp')

  if (quality) {
    url.searchParams.set('quality', quality.toString())
  }

  if (width) {
    url.searchParams.set('width', width.toString())
  }

  if ((objectFit === 'cover' || objectFit === 'contain') && cropRatio) {
    const offset = {
      bottom: '100',
      center: '50',
      left: '0',
      right: '100',
      top: '0',
    }

    const [offsetX, offsetY] = split(objectPosition.toString(), ' ')

    const cropParam = `${cropRatio},offset-x${offset[offsetX]},offset-y${offset[offsetY]}`

    url.searchParams.set('crop', cropParam)
  }

  return url.toString()
}

const ASSET_CDN_BASE = 'https://a.trendhim.com/'

// Cloudflare image options as a path segment, `{base}/{options}/{key}`, in the same order the asset CDN
// converter writes them (fit, w, h, g, q, f) so equal transforms share one cache entry
export const assetCDNLoader = (
  {
    cropRatio,
    objectFit,
    objectPosition = 'center top',
    quality = 90,
    src,
    width,
  }: ContentstackLoaderProps,
) => {
  // a bare key, or one options segment followed by the key
  const key = new URL(src).pathname.split('/').pop() || ''

  // SVGs are served as stored; options would only split the cache
  if (key.toLowerCase().endsWith('.svg')) {
    return ASSET_CDN_BASE + key
  }

  const options: string[] = []

  if ((objectFit === 'cover' || objectFit === 'contain') && cropRatio && width) {
    const offset = {
      bottom: '1',
      center: '0.5',
      left: '0',
      right: '1',
      top: '0',
    }

    const [offsetX, offsetY] = split(objectPosition.toString(), ' ')
    const [ratioWidth, ratioHeight] = split(cropRatio, ':').map(Number)

    options.push('fit=cover', `w=${width}`, `h=${Math.round((width * ratioHeight) / ratioWidth)}`, `g=${offset[offsetX]}x${offset[offsetY]}`)
  } else if (width) {
    options.push(`w=${width}`)
  }

  if (quality) {
    // the Contentstack form "75,90" is a range; the last value is the one that applies
    options.push(`q=${split(quality.toString(), ',').pop()}`)
  }

  options.push('f=auto')

  return `${ASSET_CDN_BASE}${options.join(',')}/${key}`
}

export const imageLoader = (props: ContentstackLoaderProps) => (
  props.src.startsWith(ASSET_CDN_BASE) ? assetCDNLoader(props) : contentstackLoader(props)
)

export const generateSrcSet = ({
  width,
  ...props
}: ContentstackLoaderProps) => [
  imageLoader({ width, ...props }),
  ', ',
  imageLoader({ width: width * 2, ...props }),
  ' 2x',
].join('')

export const generateProductLinkingDataImage = (src: string): string => imageLoader({
  src,
  width: 250,
})
